import React, { useState, useEffect } from "react";
import { Button } from "components";
import { ConsultantObjectivesData } from "../types";
import { useForm } from "react-hook-form";

interface ConsultantObjectivesProps {
  submit: (data: ConsultantObjectivesData) => void;
  init: ConsultantObjectivesData;
  goBack: () => void;
}

const usageOptions = [
  { label: "Hire for open roles", value: "hire_open_roles" },
  { label: "Benchmark leadership", value: "benchmark_leadership" },
  { label: "Role fit (promotion/transfer)", value: "role_fit" },
  { label: "Workforce gap analysis", value: "workforce_gap" },
];

const clientTypeOptions = [
  { label: "Startups", value: "startups" },
  { label: "SMEs", value: "smes" },
  { label: "Large Enterprises", value: "large_enterprises" },
  { label: "Public Sector", value: "public_sector" },
  { label: "Others", value: "others" },
];

const clientValueOptions = [
  { label: "Work Independently", value: "work_independently" },
  { label: "Collaborate Effectively", value: "collaborate_effectively" },
  { label: "Pursue Excellence", value: "pursue_excellence" },
  { label: "Act as Brand Ambassadors", value: "brand_ambassadors" },
  { label: "Demonstrate Passion", value: "demonstrate_passion" },
  { label: "Exhibit Charisma in Leadership", value: "exhibit_charisma" },
  { label: "Drive Financial Success", value: "drive_financial_success" },
];

const consultingRoleOptions = [
  { label: "CEO", value: "ceo" },
  { label: "COO", value: "coo" },
  { label: "CFO", value: "cfo" },
  { label: "CTO", value: "cto" },
  { label: "CMO", value: "cmo" },
  { label: "Board Member / Director", value: "board_member" },
  { label: "Managing Partner / Principal", value: "managing_partner" },
  { label: "Senior Advisor / Consultant", value: "senior_advisor" },
  { label: "Others", value: "others" },
];

const projectVolumeOptions = [
  { label: "1—10", value: "1-10" },
  { label: "11—50", value: "11-50" },
  { label: "51—100", value: "51-100" },
  { label: "100+", value: "100+" },
];

export const ConsultantObjectivesForm: React.FC<ConsultantObjectivesProps> = ({
  submit,
  init,
  goBack
}) => {
  const [formData, setFormData] = useState<ConsultantObjectivesData>(
    {
      usage: init?.usage || [],
      culture: init?.culture || [],
      roleTypes: init?.roleTypes || [],
      hrManagement: init?.hrManagement || "",
      clientTypes: init?.clientTypes || [],
      clientValues: init?.clientValues || [],
      consultingRoles: init?.consultingRoles || [],
      projectVolume: init?.projectVolume || "",
    }
  );

  // Sync form state when init changes (e.g., when navigating back from step 3)
  useEffect(() => {
    if (init) {
      setFormData({
        usage: init.usage || [],
        culture: init.culture || [],
        roleTypes: init.roleTypes || [],
        hrManagement: init.hrManagement || "",
        clientTypes: init.clientTypes || [],
        clientValues: init.clientValues || [],
        consultingRoles: init.consultingRoles || [],
        projectVolume: init.projectVolume || "",
      });
    }
  }, [init]);

  const {
    register,
    formState: { errors },
  } = useForm();

  const handleMultiSelect = (category: keyof ConsultantObjectivesData, value: string) => {
    if (category === 'projectVolume') {
      setFormData(prev => ({ ...prev, [category]: value }));
      return;
    }

    const currentValues = Array.isArray(formData[category]) ? formData[category] as string[] : [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];

    setFormData(prev => ({ ...prev, [category]: newValues }));
  };

  const handleSubmit = () => {
    submit(formData);
  };

  return (
    <div className="max-h-[380px] overflow-y-auto scrollbar-none">
      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          How Will You Use Precision?
        </label>
        <div className="flex flex-wrap gap-2">
          {usageOptions?.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('usage', option?.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${formData?.usage?.includes(option.value)
                ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF] hover:bg-gray-200'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>


      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Which types of clients do you usually support?
        </label>
        <div className="flex flex-wrap gap-3">
          {clientTypeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('clientTypes', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${formData?.clientTypes?.includes(option.value)
                ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Which values do your clients most often want to reinforce?
        </label>
        <div className="flex flex-wrap gap-3">
          {clientValueOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('clientValues', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${formData?.clientValues?.includes(option.value)
                ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Which consulting roles do you assess?
        </label>
        <div className="flex flex-wrap gap-3">
          {consultingRoleOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('consultingRoles', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${formData?.consultingRoles?.includes(option.value)
                ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          className="max-w-sm flex-1 border !border-[#012169] !text-[#012169] bg-white"
          type="outline"
          onClick={goBack}
        >
          Back
        </Button>
        <Button
          className="max-w-sm flex-1 !bg-[#012169] !border-none text-white"
          type="fill"
          onClick={handleSubmit}
        >
          Next
        </Button>
      </div>
    </div>
  );
};